import { useEffect, useState } from "react";
import Portal from "../../components/common/Portal";
import { Search, BookOpen, Clock } from "lucide-react";
import DashboardLayout from "../../layouts/DashboardLayout";
import useFeatures from "../../hooks/useFeatures";
import api from "../../api";
import ToolkitPreview, { TYPE_LABEL, TYPE_STYLE } from "../../components/clinical/ToolkitPreview";
import AssignToolModal from "../../components/clinical/AssignToolModal";

// Toolkit library: browse, preview and give items to a patient.
export default function Toolkit() {
  const features = useFeatures();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [type, setType] = useState("");
  const [preview, setPreview] = useState(null);
  const [assignCode, setAssignCode] = useState(null);

  useEffect(() => { if (features.has("toolkit")) api.getToolkit().then((r) => setItems(r.data || [])).catch(() => setItems([])); }, [features.ready]); // eslint-disable-line

  if (features.ready && !features.has("toolkit")) return <DashboardLayout><div className="p-8 text-secondary-600">The toolkit isn't switched on for your organisation yet.</div></DashboardLayout>;

  const categories = [...new Set((items || []).map((i) => i.category).filter(Boolean))].sort();
  const shown = (items || []).filter((i) => (!category || i.category === category) && (!type || i.type === type)
    && (!q.trim() || `${i.title} ${i.description} ${(i.tags || []).join(" ")}`.toLowerCase().includes(q.trim().toLowerCase())));

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-5">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-secondary-900"><BookOpen className="w-6 h-6 text-primary-600" /> Toolkit</h1>
          <p className="text-sm text-secondary-600">Worksheets, exercises and readings to give patients between sessions. They fill them in from My Care in the patient app, and you see their answers on the patient file.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-secondary-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search: sleep, worry, grief, breathing…" aria-label="Search the toolkit"
              className="w-full rounded-lg border border-secondary-300 bg-white py-2 pl-9 pr-3 text-sm" />
          </div>
          <select value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Topic" className="rounded-lg border border-secondary-300 bg-white px-3 py-2 text-sm">
            <option value="">All topics</option>{categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={type} onChange={(e) => setType(e.target.value)} aria-label="Type" className="rounded-lg border border-secondary-300 bg-white px-3 py-2 text-sm">
            <option value="">All types</option>{Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        {items === null ? <p className="text-sm text-secondary-500">Loading…</p> : (
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((i) => (
              <li key={i.code} className="flex flex-col rounded-xl border border-secondary-200 bg-white p-4">
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${TYPE_STYLE[i.type] || ""}`}>{TYPE_LABEL[i.type] || i.type}</span>
                  <span className="text-xs text-secondary-600">{i.category}</span>
                  {i.minutes && <span className="ml-auto inline-flex items-center gap-1 text-xs text-secondary-600"><Clock className="w-3.5 h-3.5" />{i.minutes} min</span>}
                </div>
                <h3 className="mt-2 text-sm font-bold text-secondary-900">{i.title}</h3>
                <p className="mt-1 flex-1 text-xs text-secondary-600">{i.description}</p>
                <div className="mt-3 flex gap-2">
                  <button type="button" onClick={() => api.getToolkitItem(i.code).then((r) => setPreview(r.data))} className="rounded-lg border border-secondary-300 px-3 py-1.5 text-xs font-semibold">Preview</button>
                  {i.audience !== "clinician" && <button type="button" onClick={() => setAssignCode(i.code)} className="rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white">Assign as homework</button>}
                </div>
              </li>
            ))}
            {shown.length === 0 && <li className="text-sm text-secondary-500">Nothing matches.</li>}
          </ul>
        )}
      </div>
      {preview && (
        <Portal><div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setPreview(null)}>
          <div className="w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-bold text-secondary-900">{preview.title}</h3>
              <button type="button" onClick={() => setPreview(null)} className="text-sm text-secondary-500">Close</button>
            </div>
            <ToolkitPreview item={preview} />
          </div>
        </div></Portal>
      )}
      {assignCode && <AssignToolModal initialCode={assignCode} onClose={() => setAssignCode(null)} />}
    </DashboardLayout>
  );
}
